import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { X, Search, Trash2, FilePlus2, AlertTriangle, Plus } from 'lucide-react';
import { supabase } from '../../supabase';
import { applyActiveProductsFilter } from '../../utils/productStatus';
import { getLocalDateKey } from '../../utils/localDateTime';
import {
  calcularTotalVentaAdministrativa,
  crearVentaAdministrativa,
  registrarCobroVentaAdministrativa,
  COBRO,
  validarVentaAdministrativa,
  MAX_DIAS_ATRAS,
  MOTIVO_MIN_LENGTH,
} from '../../utils/ventaAdministrativa';

// Venta administrativa: va a cuenta corriente del cliente, no entra al arqueo
// de caja y descuenta stock solo si se pide. Reglas en utils/ventaAdministrativa.js.

const formatMoney = (value) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(value) || 0);

const shiftDays = (dateKey, days) => {
  const [y, m, d] = dateKey.split('-').map(Number);
  return getLocalDateKey(new Date(y, m - 1, d + days));
};

const escapeIlike = (value) => String(value || '').replace(/[%_,()]/g, ' ').trim();

const labelClass = 'text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 block';
const inputClass = 'w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#51A7FD]/30';

export default function VentaAdministrativaModal({
  open,
  onClose,
  onCreated,
  profile,
  organization,
  sitios = [],
  defaultSiteId = '',
  billingEntityId = null,
  billingEntityName = '',
  effectiveModules = null,
  isSuperAdmin = false,
}) {
  const orgId = profile?.organization_id;
  const hoy = getLocalDateKey();

  const [cliente, setCliente] = useState(null);
  const [clienteQuery, setClienteQuery] = useState('');
  const [clienteResults, setClienteResults] = useState([]);
  const [siteId, setSiteId] = useState(defaultSiteId || '');
  const [productQuery, setProductQuery] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [items, setItems] = useState([]);
  const [afectaStock, setAfectaStock] = useState(true);
  const [fecha, setFecha] = useState(hoy);
  const [motivo, setMotivo] = useState('');
  const [emitirAhora, setEmitirAhora] = useState(false);
  const [ctaCteMethod, setCtaCteMethod] = useState(null);
  // Consumidor Final: no tiene cuenta corriente, se elige como se cobro.
  const [consumidorFinal, setConsumidorFinal] = useState(false);
  const [cobro, setCobro] = useState(null);
  const [metodosBanco, setMetodosBanco] = useState([]);
  const [metodoBancoId, setMetodoBancoId] = useState('');
  const [saving, setSaving] = useState(false);
  // Venta creada pero sin registrar en cuenta corriente: se ofrece reintentar.
  const [pendienteCtaCte, setPendienteCtaCte] = useState(null);
  const clienteReq = useRef(0);
  const productReq = useRef(0);

  const allowNegativeStock = Boolean(organization?.allow_negative_stock);

  // Reset al abrir
  useEffect(() => {
    if (!open) return;
    setCliente(null);
    setClienteQuery('');
    setClienteResults([]);
    setSiteId(defaultSiteId || sitios[0]?.id || '');
    setProductQuery('');
    setProductResults([]);
    setItems([]);
    setAfectaStock(true);
    setFecha(getLocalDateKey());
    setMotivo('');
    setEmitirAhora(false);
    setPendienteCtaCte(null);
    setConsumidorFinal(false);
    setCobro(null);
    setMetodoBancoId('');
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Metodo "cuenta corriente" de la org, si existe: la devolucion lo propone
  // como metodo original y cae en el circuito de nota de credito.
  useEffect(() => {
    if (!open || !orgId) return;
    supabase
      .from('payment_methods')
      .select('id, name, system_code, bank_id, commission_percentage, accreditation_days, accreditation_business_days')
      .eq('organization_id', orgId)
      .eq('is_active', true)
      .then(({ data }) => {
        const match = (data || []).find((m) =>
          String(m.system_code || '').toLowerCase().includes('cuenta_corriente')
          || String(m.name || '').toLowerCase().includes('cuenta corriente'));
        setCtaCteMethod(match || null);
        setMetodosBanco((data || []).filter((m) => m.bank_id));
      });
  }, [open, orgId]);

  // Buscador de clientes (server-side: hay orgs con miles)
  useEffect(() => {
    if (!open || !orgId || cliente) return undefined;
    const term = escapeIlike(clienteQuery);
    if (term.length < 2) { setClienteResults([]); return undefined; }
    const reqId = ++clienteReq.current;
    const t = setTimeout(async () => {
      const { data, error } = await supabase
        .from('clients')
        .select('id, full_name, tax_condition, doc_number, payment_condition_days, default_payment_condition')
        .eq('organization_id', orgId)
        .not('is_active', 'is', false)
        .or(`full_name.ilike.%${term}%,doc_number.ilike.%${term}%`)
        .order('full_name')
        .limit(15);
      if (reqId !== clienteReq.current) return;
      if (error) { console.error(error); return; }
      setClienteResults((data || []).filter((c) => String(c.full_name || '').trim().toLowerCase() !== 'consumidor final'));
    }, 250);
    return () => clearTimeout(t);
  }, [clienteQuery, open, orgId, cliente]);

  // Buscador de productos: activos, sin combos (ver validarVentaAdministrativa)
  useEffect(() => {
    if (!open || !orgId) return undefined;
    const term = escapeIlike(productQuery);
    if (term.length < 2) { setProductResults([]); return undefined; }
    const reqId = ++productReq.current;
    const t = setTimeout(async () => {
      const query = supabase
        .from('products')
        .select('id, name, barcode, sell_price, iva_percentage, product_type, active, is_active, archived_at, stock_levels(site_id, quantity)')
        .eq('organization_id', orgId)
        .or(`name.ilike.%${term}%,barcode.ilike.%${term}%`)
        .order('name')
        .limit(20);
      const { data, error } = await applyActiveProductsFilter(query);
      if (reqId !== productReq.current) return;
      if (error) { console.error(error); return; }
      setProductResults((data || []).filter((p) => p.product_type !== 'Combo'));
    }, 250);
    return () => clearTimeout(t);
  }, [productQuery, open, orgId]);

  const stockEnSucursal = (product) => {
    const row = (product?.stock_levels || []).find((s) => String(s.site_id) === String(siteId));
    return row ? Number(row.quantity) || 0 : 0;
  };

  const agregarProducto = (product) => {
    setItems((prev) => {
      const existing = prev.find((it) => it.product_id === product.id);
      if (existing) {
        return prev.map((it) => (it.product_id === product.id ? { ...it, quantity: Number(it.quantity) + 1 } : it));
      }
      const iva = Number(product.iva_percentage);
      return [...prev, {
        key: product.id,
        product_id: product.id,
        name: product.name,
        quantity: 1,
        unit_price: Number(product.sell_price) || 0,
        iva_percentage: Number.isNaN(iva) ? 21 : iva,
        product_type: product.product_type || null,
        stock_levels: product.stock_levels || [],
      }];
    });
    setProductQuery('');
    setProductResults([]);
  };

  // "Productos Varios": item manual sin producto del catalogo, importe libre,
  // como el del POS. No mueve stock. Se pueden agregar varios.
  const miscSeq = useRef(0);
  const agregarVarios = () => {
    miscSeq.current += 1;
    setItems((prev) => [...prev, {
      key: `misc-${miscSeq.current}`,
      is_misc: true,
      product_id: null,
      name: 'Productos Varios',
      quantity: 1,
      unit_price: '',
      iva_percentage: 21,
    }]);
  };

  const actualizarItem = (key, field, value) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, [field]: value } : it)));
  };

  const total = useMemo(() => calcularTotalVentaAdministrativa(items), [items]);

  const faltantesStock = useMemo(() => {
    if (!afectaStock || allowNegativeStock) return [];
    return items.filter((it) => !it.is_misc && Number(it.quantity) > stockEnSucursal(it));
  }, [items, afectaStock, allowNegativeStock, siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const clienteVenta = consumidorFinal ? null : cliente;
  const metodoBanco = metodosBanco.find((m) => m.id === metodoBancoId) || null;
  const destinoLabel = consumidorFinal
    ? (cobro === COBRO.BANCO ? 'Total cobrado por banco' : 'Total')
    : 'Total a cuenta corriente';
  const errores = validarVentaAdministrativa({ cliente: clienteVenta, siteId, items, motivo, fecha, hoy, cobro, metodoBanco });
  const puedeGuardar = errores.length === 0 && faltantesStock.length === 0 && !saving;

  const guardar = async () => {
    if (errores.length > 0) { toast.error(errores[0]); return; }
    if (faltantesStock.length > 0) {
      toast.error(`Sin stock suficiente en la sucursal: ${faltantesStock[0].name}`);
      return;
    }
    setSaving(true);
    const toastId = toast.loading('Guardando venta administrativa...');
    try {
      const { saleId } = await crearVentaAdministrativa({
        organizationId: orgId,
        userId: profile?.id || null,
        profile,
        isSuperAdmin,
        effectiveModules,
        cliente: clienteVenta,
        siteId,
        items,
        afectaStock,
        fecha,
        motivo,
        billingEntityId,
        ctaCteMethod,
        cobro,
        metodoBanco,
        allowNegativeStock,
      });
      toast.success(consumidorFinal ? 'Venta cargada.' : 'Venta cargada en la cuenta corriente del cliente.', { id: toastId });
      onCreated?.(saleId, { emitir: emitirAhora });
    } catch (error) {
      console.error('Venta administrativa:', error);
      toast.error(error?.message || 'No se pudo guardar la venta.', { id: toastId, duration: 8000 });
      if (error?.partial && error?.retryArgs) setPendienteCtaCte(error.retryArgs);
    } finally {
      setSaving(false);
    }
  };

  const reintentarCtaCte = async () => {
    if (!pendienteCtaCte) return;
    setSaving(true);
    const toastId = toast.loading('Registrando el cobro...');
    try {
      await registrarCobroVentaAdministrativa(pendienteCtaCte);
      toast.success('Listo: el cobro quedó registrado.', { id: toastId });
      const saleId = pendienteCtaCte.saleId;
      setPendienteCtaCte(null);
      onCreated?.(saleId, { emitir: emitirAhora });
    } catch (error) {
      toast.error(error?.message || 'Sigue fallando. Probá de nuevo en un momento.', { id: toastId, duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl border border-slate-200 p-4 sm:p-6 relative my-4">
        <button onClick={() => (pendienteCtaCte ? onCreated?.(pendienteCtaCte.saleId, { emitir: false }) : onClose())} disabled={saving} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600" aria-label="Cerrar"><X size={20} /></button>

        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 bg-[#0B2A36] rounded-xl flex items-center justify-center"><FilePlus2 size={18} className="text-[#3FF2AA]" /></div>
          <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight">Venta administrativa</h3>
        </div>
        <p className="text-[11px] text-slate-500 mb-4">
          Aparece en ventas, facturación y reportes. <b>No entra al arqueo de caja.</b> A un cliente con nombre va a su cuenta corriente; a Consumidor Final elegís cómo se cobró.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div className="md:col-span-2 relative">
            <label className={labelClass}>Cliente</label>
            {consumidorFinal ? (
              <div className="flex items-center justify-between gap-2 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl">
                <div className="text-xs font-black text-slate-800 uppercase">Consumidor Final</div>
                <button onClick={() => { setConsumidorFinal(false); setCobro(null); }} disabled={saving} className="text-[10px] font-black uppercase text-[#3B82F6]">Cambiar</button>
              </div>
            ) : cliente ? (
              <div className="flex items-center justify-between gap-2 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl">
                <div className="min-w-0">
                  <div className="text-xs font-black text-slate-800 uppercase truncate">{cliente.full_name}</div>
                  <div className="text-[10px] text-slate-400">{cliente.tax_condition || 'Sin condición fiscal'}{cliente.doc_number ? ` · ${cliente.doc_number}` : ''}</div>
                </div>
                <button onClick={() => setCliente(null)} disabled={saving} className="text-[10px] font-black uppercase text-[#3B82F6]">Cambiar</button>
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <input className={inputClass} placeholder="Buscar por nombre o CUIT/DNI..." value={clienteQuery} onChange={(e) => setClienteQuery(e.target.value)} />
                  <button onClick={() => { setConsumidorFinal(true); setClienteResults([]); }} disabled={saving} className="shrink-0 px-3 bg-white border border-slate-200 rounded-xl text-[10px] font-black uppercase text-slate-600 hover:bg-slate-50">
                    Consumidor Final
                  </button>
                </div>
                {clienteResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
                    {clienteResults.map((c) => (
                      <button key={c.id} onClick={() => { setCliente(c); setClienteResults([]); }} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-xs font-bold text-slate-700 uppercase">
                        {c.full_name}<span className="ml-2 text-[10px] font-medium text-slate-400 normal-case">{c.doc_number || ''}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div>
            <label className={labelClass}>Sucursal</label>
            <select className={inputClass} value={siteId} onChange={(e) => setSiteId(e.target.value)} disabled={saving}>
              <option value="">Seleccionar</option>
              {sitios.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
            </select>
          </div>
        </div>

        {consumidorFinal && (
          <div className="mb-3 p-3 bg-slate-50 border border-slate-200 rounded-2xl">
            <label className={labelClass}>¿Cómo se cobró?</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <label className="flex-1 flex items-start gap-2 cursor-pointer bg-white border border-slate-200 rounded-xl px-3 py-2">
                <input type="radio" name="cobro-va" className="mt-0.5" checked={cobro === COBRO.SIN_MOVIMIENTO} onChange={() => setCobro(COBRO.SIN_MOVIMIENTO)} disabled={saving} />
                <span className="text-xs font-bold text-slate-700">
                  Sin movimiento de plata
                  <span className="block text-[10px] font-medium text-slate-400">Solo registra la venta y la factura. La plata ya se registró por otro lado.</span>
                </span>
              </label>
              <label className="flex-1 flex items-start gap-2 cursor-pointer bg-white border border-slate-200 rounded-xl px-3 py-2">
                <input type="radio" name="cobro-va" className="mt-0.5" checked={cobro === COBRO.BANCO} onChange={() => setCobro(COBRO.BANCO)} disabled={saving || metodosBanco.length === 0} />
                <span className="text-xs font-bold text-slate-700">
                  Cobrada por banco
                  <span className="block text-[10px] font-medium text-slate-400">
                    {metodosBanco.length === 0 ? 'No hay medios de pago asociados a un banco.' : 'Entra a Bancos y al libro diario, no a la caja.'}
                  </span>
                </span>
              </label>
            </div>
            {cobro === COBRO.BANCO && (
              <select className={`${inputClass} mt-2`} value={metodoBancoId} onChange={(e) => setMetodoBancoId(e.target.value)} disabled={saving}>
                <option value="">Elegí el medio de pago</option>
                {metodosBanco.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            )}
          </div>
        )}

        <div className="relative mb-2">
          <label className={labelClass}>Productos</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className={`${inputClass} pl-8`} placeholder="Buscar producto por nombre o código..." value={productQuery} onChange={(e) => setProductQuery(e.target.value)} disabled={saving} />
          </div>
          {productResults.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
              {productResults.map((p) => (
                <button key={p.id} onClick={() => agregarProducto(p)} className="w-full flex justify-between gap-2 text-left px-3 py-2 hover:bg-slate-50 text-xs font-bold text-slate-700">
                  <span className="truncate uppercase">{p.name}</span>
                  <span className="shrink-0 text-slate-400 font-medium">{formatMoney(p.sell_price)} · stock {stockEnSucursal(p)}</span>
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between gap-2 mt-1">
            <p className="text-[10px] text-slate-400">Los combos no se pueden cargar acá.</p>
            <button onClick={agregarVarios} disabled={saving} className="flex items-center gap-1 px-2.5 py-1.5 bg-amber-50 border border-amber-100 rounded-lg text-[10px] font-black uppercase text-amber-700 hover:bg-amber-100">
              <Plus size={12} /> Productos varios
            </button>
          </div>
        </div>

        {items.length > 0 && (
          <div className="border border-slate-100 rounded-2xl overflow-x-auto mb-3">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[9px] font-black uppercase tracking-widest text-slate-400">
                <tr>
                  <th className="text-left px-3 py-2">Producto</th>
                  <th className="px-2 py-2 w-20">Cant.</th>
                  <th className="px-2 py-2 w-28">Precio</th>
                  <th className="text-right px-3 py-2 w-28">Subtotal</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const sinStock = !it.is_misc && afectaStock && !allowNegativeStock && Number(it.quantity) > stockEnSucursal(it);
                  return (
                    <tr key={it.key} className={`border-t border-slate-100 ${it.is_misc ? 'bg-amber-50/40' : ''}`}>
                      <td className="px-3 py-2 font-bold text-slate-700 uppercase">
                        {it.is_misc ? (
                          <div className="flex flex-col gap-1">
                            <input className={inputClass} value={it.name} onChange={(e) => actualizarItem(it.key, 'name', e.target.value)} placeholder="Descripción" maxLength={120} disabled={saving} />
                            <select className={`${inputClass} py-1.5`} value={it.iva_percentage} onChange={(e) => actualizarItem(it.key, 'iva_percentage', Number(e.target.value))} disabled={saving} title="Alícuota de IVA">
                              <option value={21}>IVA 21%</option>
                              <option value={10.5}>IVA 10,5%</option>
                              <option value={27}>IVA 27%</option>
                              <option value={0}>IVA 0% / exento</option>
                            </select>
                          </div>
                        ) : it.name}
                        {sinStock && <div className="text-[10px] font-bold text-rose-500 normal-case">Stock en sucursal: {stockEnSucursal(it)}</div>}
                      </td>
                      <td className="px-2 py-2 align-top"><input type="number" min="0" step="any" className={inputClass} value={it.quantity} onChange={(e) => actualizarItem(it.key, 'quantity', e.target.value)} disabled={saving} /></td>
                      <td className="px-2 py-2 align-top"><input type="number" min="0" step="0.01" className={inputClass} value={it.unit_price} placeholder={it.is_misc ? 'Importe' : undefined} autoFocus={it.is_misc} onChange={(e) => actualizarItem(it.key, 'unit_price', e.target.value)} disabled={saving} /></td>
                      <td className="px-3 py-2 text-right font-black text-slate-800 align-top">{formatMoney((Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}</td>
                      <td className="pr-2 align-top pt-3"><button onClick={() => setItems((prev) => prev.filter((x) => x.key !== it.key))} disabled={saving} className="text-slate-300 hover:text-rose-500" aria-label="Quitar"><Trash2 size={14} /></button></td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50">
                  <td colSpan={3} className="px-3 py-2 text-right text-[10px] font-black uppercase tracking-widest text-slate-500">{destinoLabel}</td>
                  <td className="px-3 py-2 text-right font-black text-slate-900">{formatMoney(total)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div>
            <label className={labelClass}>Fecha</label>
            <input type="date" className={inputClass} value={fecha} min={shiftDays(hoy, -MAX_DIAS_ATRAS)} max={hoy} onChange={(e) => setFecha(e.target.value)} disabled={saving} />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Motivo (obligatorio)</label>
            <input className={inputClass} placeholder="Ej: mayorista, cobra por transferencia a 30 días" value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={300} disabled={saving} />
            <p className="text-[10px] text-slate-400 mt-1">Mínimo {MOTIVO_MIN_LENGTH} caracteres. Queda en el historial de la venta.</p>
          </div>
        </div>

        <div className="flex flex-col gap-2 mb-4">
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={afectaStock} onChange={(e) => setAfectaStock(e.target.checked)} disabled={saving} />
            <span className="text-xs font-bold text-slate-700">
              Descontar stock
              <span className="block text-[10px] font-medium text-slate-400">Desactivalo solo si la mercadería ya se descontó (remito, otra venta, ajuste).</span>
            </span>
          </label>
          {!afectaStock && (
            <div className="flex items-center gap-2 text-[11px] font-bold text-amber-600 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
              <AlertTriangle size={14} /> No se va a mover el stock. Si la mercadería todavía no salió, el inventario va a quedar alto.
            </div>
          )}
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={emitirAhora} onChange={(e) => setEmitirAhora(e.target.checked)} disabled={saving} />
            <span className="text-xs font-bold text-slate-700">
              Emitir la factura en ARCA al guardar
              <span className="block text-[10px] font-medium text-slate-400">
                {billingEntityName
                  ? `Emisor: ${billingEntityName}. Se cambia arriba, en "Emisor por defecto".`
                  : 'No hay emisor por defecto: elegilo arriba, en "Emisor por defecto", antes de emitir.'}
              </span>
            </span>
          </label>
        </div>

        {errores.length > 0 && (items.length > 0 || cliente || consumidorFinal) && (
          <p className="text-[11px] font-bold text-rose-500 mb-3">{errores[0]}</p>
        )}

        {pendienteCtaCte ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-2 text-[11px] font-bold text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-3 py-2">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              La venta #{String(pendienteCtaCte.saleId).slice(0, 8).toUpperCase()} se guardó, pero falta registrar {pendienteCtaCte.cobro === COBRO.BANCO ? 'el ingreso en el banco' : 'la deuda en la cuenta corriente del cliente'}. No la cargues de nuevo: reintentá.
            </div>
            <button onClick={reintentarCtaCte} disabled={saving} className="py-3 bg-rose-600 text-white rounded-xl font-black text-xs uppercase tracking-widest disabled:opacity-40">
              {saving ? 'Reintentando...' : 'Reintentar registro del cobro'}
            </button>
          </div>
        ) : (
        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={onClose} disabled={saving} className="flex-1 py-3 bg-white border rounded-xl text-slate-600 font-bold text-xs uppercase">Cancelar</button>
          <button onClick={guardar} disabled={!puedeGuardar} className="flex-1 py-3 bg-[#0B2A36] text-white rounded-xl font-black text-xs uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? 'Guardando...' : `Guardar ${total > 0 ? formatMoney(total) : ''}`}
          </button>
        </div>
        )}
      </div>
    </div>
  );
}
